import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useMasterData } from '../contexts/MasterDataContext';
import axios from 'axios';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LabelList } from 'recharts';
import { DollarSign, CalendarCheck, Filter, Building2, Gamepad2, MonitorSmartphone, Clock, Download } from 'lucide-react';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear, endOfYear, subDays, subMonths } from 'date-fns';
import { Button } from '../components/ui/button';

import { BACKEND_URL, API } from '../utils/apiConfig';

const COLORS = ['#16a34a', '#15803d', '#14532d', '#22c55e', '#4ade80', '#86efac'];

const formatCompactINR = (n) => {
  const v = Number(n || 0);
  const sign = v < 0 ? '-' : '';
  const abs = Math.abs(v);
  if (abs >= 100000) {
    const l = abs / 100000;
    return `${sign}₹${l % 1 === 0 ? l.toFixed(0) : l.toFixed(1)}L`;
  }
  if (abs >= 1000) {
    const k = abs / 1000;
    return `${sign}₹${k % 1 === 0 ? k.toFixed(0) : k.toFixed(1)}K`;
  }
  return `${sign}₹${abs.toLocaleString('en-IN')}`;
};

const DATE_FILTER_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'this_week', label: 'This Week' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'last_2_months', label: 'Last 2 Months' },
  { value: 'last_3_months', label: 'Last 3 Months' },
  { value: 'last_6_months', label: 'Last 6 Months' },
  { value: 'this_year', label: 'This Year' },
  { value: 'custom', label: 'Custom Range' },
];

const DAY_OF_WEEK_OPTIONS = [
  { value: 'all', label: 'All Days' },
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' },
  { value: 'sunday', label: 'Sunday' },
  { value: 'mon_thu', label: 'Mon–Thu' },
  { value: 'mon_fri', label: 'Mon–Fri' },
  { value: 'fri_sun', label: 'Fri–Sun' },
  { value: 'sat_sun', label: 'Sat–Sun' },
];

const TIME_PERIOD_OPTIONS = [
  { value: 'all', label: 'All Times' },
  { value: 'morning', label: 'Morning (6AM-12PM)' },
  { value: 'afternoon', label: 'Afternoon (12PM-5PM)' },
  { value: 'evening', label: 'Evening (5PM-9PM)' },
  { value: 'latenight', label: 'Late Night (9PM-2AM)' },
  { value: '4pm_12am', label: 'Evening Peak (4PM-12AM)' },
  { value: '6pm_12am', label: 'Night (6PM-12AM)' },
];

const PLATFORM_OPTIONS = [
  { value: 'all', label: 'All Platforms' },
  { value: 'Calling', label: 'Calling' },
  { value: 'App', label: 'App' },
  { value: 'WhatsApp', label: 'WhatsApp' },
  { value: 'Khelomore', label: 'Khelomore' },
  { value: 'District', label: 'District' },
  { value: 'Hudle', label: 'Hudle' },
  { value: 'Walk-in', label: 'Walk-in' },
  { value: 'Others', label: 'Others' },
];

const BULK_FILTER_OPTIONS = [
  { value: 'without', label: 'Without Bulk Booking' },
  { value: 'with', label: 'With Bulk Booking' },
  { value: 'only', label: 'Only Bulk Booking' },
];

export default function ReportsPage() {
  const { venues, allGames } = useMasterData();
  const [loading, setLoading] = useState(true);
  const [dateFilterType, setDateFilterType] = useState('today');
  const [startDate, setStartDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [dailyReport, setDailyReport] = useState(null);
  const [venueReport, setVenueReport] = useState(null);
  const [gameReport, setGameReport] = useState(null);
  const [platformReport, setPlatformReport] = useState(null);
  
  // Additional filters
  const [venueFilter, setVenueFilter] = useState('all');
  const [gameFilter, setGameFilter] = useState('all');
  const [platformFilter, setPlatformFilter] = useState('all');
  const [timeFilter, setTimeFilter] = useState('all');
  const [dayFilter, setDayFilter] = useState('all');
  const [bulkFilter, setBulkFilter] = useState('without');
  const [exporting, setExporting] = useState(false);

  const handleExportToExcel = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({
        start_date: startDate,
        end_date: endDate,
      });
      if (venueFilter && venueFilter !== 'all') params.append('venue', venueFilter);
      if (gameFilter && gameFilter !== 'all') params.append('game', gameFilter);
      if (platformFilter && platformFilter !== 'all') params.append('platform', platformFilter);
      if (timeFilter && timeFilter !== 'all') params.append('time_period', timeFilter);
      if (dayFilter && dayFilter !== 'all') params.append('day_filter', dayFilter);
      params.append('bulk_filter', bulkFilter);
      const url = `${API}/reports/export?${params.toString()}`;
      const response = await axios.get(url, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', `maks_report_${startDate}_${endDate}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      toast.success('Report exported');
    } catch (err) {
      console.error('Export failed', err);
      toast.error('Failed to export report');
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    // Calculate date range based on filter type
    const today = new Date();
    let newStartDate, newEndDate;

    switch (dateFilterType) {
      case 'today':
        newStartDate = format(today, 'yyyy-MM-dd');
        newEndDate = format(today, 'yyyy-MM-dd');
        break;
      case 'yesterday': {
        const y = subDays(today, 1);
        newStartDate = format(y, 'yyyy-MM-dd');
        newEndDate = format(y, 'yyyy-MM-dd');
        break;
      }
      case 'this_week':
        newStartDate = format(startOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd');
        newEndDate = format(endOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd');
        break;
      case 'this_month':
        newStartDate = format(startOfMonth(today), 'yyyy-MM-dd');
        newEndDate = format(endOfMonth(today), 'yyyy-MM-dd');
        break;
      case 'last_month':
        newStartDate = format(startOfMonth(subMonths(today, 1)), 'yyyy-MM-dd');
        newEndDate = format(endOfMonth(subMonths(today, 1)), 'yyyy-MM-dd');
        break;
      case 'last_2_months':
        newStartDate = format(startOfMonth(subMonths(today, 2)), 'yyyy-MM-dd');
        newEndDate = format(endOfMonth(subMonths(today, 1)), 'yyyy-MM-dd');
        break;
      case 'last_3_months':
        newStartDate = format(startOfMonth(subMonths(today, 3)), 'yyyy-MM-dd');
        newEndDate = format(endOfMonth(subMonths(today, 1)), 'yyyy-MM-dd');
        break;
      case 'last_6_months':
        newStartDate = format(startOfMonth(subMonths(today, 6)), 'yyyy-MM-dd');
        newEndDate = format(endOfMonth(subMonths(today, 1)), 'yyyy-MM-dd');
        break;
      case 'this_year':
        newStartDate = format(startOfYear(today), 'yyyy-MM-dd');
        newEndDate = format(endOfYear(today), 'yyyy-MM-dd');
        break;
      case 'custom':
        // Keep existing dates for custom
        return;
      default:
        newStartDate = format(today, 'yyyy-MM-dd');
        newEndDate = format(today, 'yyyy-MM-dd');
    }

    setStartDate(newStartDate);
    setEndDate(newEndDate);
  }, [dateFilterType]);

  useEffect(() => {
    fetchReports();
  }, [startDate, endDate, venueFilter, gameFilter, platformFilter, timeFilter, dayFilter, bulkFilter]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      // Build query params with filters
      const params = new URLSearchParams();
      params.append('start_date', startDate);
      params.append('end_date', endDate);
      if (venueFilter !== 'all') params.append('venue', venueFilter);
      if (gameFilter !== 'all') params.append('game', gameFilter);
      if (platformFilter !== 'all') params.append('platform', platformFilter);
      if (timeFilter !== 'all') params.append('time_period', timeFilter);
      if (dayFilter !== 'all') params.append('day_filter', dayFilter);
      params.append('bulk_filter', bulkFilter);
      
      const queryString = params.toString();
      
      const [daily, venue, game, platform] = await Promise.all([
        axios.get(`${API}/reports/daily?date=${startDate}&end_date=${endDate}&${queryString}`),
        axios.get(`${API}/reports/venue?${queryString}`),
        axios.get(`${API}/reports/game?${queryString}`),
        axios.get(`${API}/reports/platform?${queryString}`),
      ]);

      setDailyReport(daily.data);
      setVenueReport(venue.data);
      setGameReport(game.data);
      setPlatformReport(platform.data);
    } catch (error) {
      console.error('Failed to fetch reports:', error);
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const venueChartData = venueReport
    ? Object.entries(venueReport.venues).map(([name, data]) => ({
        name,
        revenue: data.revenue,
        bookings: data.bookings,
      }))
    : [];

  const gameChartData = gameReport
    ? Object.entries(gameReport.games).map(([name, data]) => ({
        name,
        revenue: data.revenue,
        bookings: data.bookings,
      }))
    : [];

  const paymentBreakdownData = dailyReport?.payment_breakdown
    ? Object.entries(dailyReport.payment_breakdown).map(([name, value]) => ({
        name,
        value,
      }))
    : [];

  const platformChartData = platformReport?.platforms
    ? Object.entries(platformReport.platforms).map(([name, data]) => ({
        name: name || 'Others',
        revenue: data.revenue,
        bookings: data.bookings,
      }))
    : [];

  const getDateRangeLabel = () => {
    if (startDate === endDate) {
      return format(new Date(startDate), 'MMM d, yyyy');
    }
    return `${format(new Date(startDate), 'MMM d')} - ${format(new Date(endDate), 'MMM d, yyyy')}`;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="reports-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="text-center sm:text-left">
            <h1 className="font-heading font-bold text-3xl text-slate-900 tracking-tight">
              Reports & Analytics
            </h1>
            <p className="text-slate-600 mt-1">View revenue and booking insights • {getDateRangeLabel()}</p>
          </div>
          <Button
            onClick={handleExportToExcel}
            disabled={exporting}
            variant="outline"
            data-testid="reports-export-excel-btn"
            className="gap-2"
          >
            <Download className={`w-4 h-4 ${exporting ? 'animate-pulse' : ''}`} />
            {exporting ? 'Exporting…' : 'Export to Excel'}
          </Button>
        </div>

        <Card className="p-6 border-slate-200">
          <div className="flex flex-col gap-4 mb-6">
            <div className="flex items-center justify-between">
              <h2 className="font-heading font-bold text-xl text-slate-900 flex items-center gap-2">
                <Filter className="w-5 h-5 text-slate-500" />
                Revenue Report
              </h2>
            </div>
            
            {/* Filter Row 1: Date Filters */}
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <Label className="text-slate-700 font-medium whitespace-nowrap">Period:</Label>
                <Select value={dateFilterType} onValueChange={setDateFilterType}>
                  <SelectTrigger className="w-36" data-testid="date-filter-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DATE_FILTER_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {dateFilterType === 'custom' && (
                <>
                  <div className="flex items-center gap-2">
                    <Label className="text-slate-700 font-medium">From:</Label>
                    <Input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-auto"
                      data-testid="start-date"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-slate-700 font-medium">To:</Label>
                    <Input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="w-auto"
                      data-testid="end-date"
                    />
                  </div>
                </>
              )}
            </div>
            
            {/* Filter Row 2: Venue, Game, Platform, Time */}
            <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-slate-100">
              {/* Venue Filter */}
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-slate-500" />
                <Select value={venueFilter} onValueChange={setVenueFilter}>
                  <SelectTrigger className="w-40" data-testid="venue-filter">
                    <SelectValue placeholder="All Venues" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Venues</SelectItem>
                    {venues.map((venue) => (
                      <SelectItem key={venue.id || venue.name} value={venue.name}>
                        {venue.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Game Filter */}
              <div className="flex items-center gap-2">
                <Gamepad2 className="w-4 h-4 text-slate-500" />
                <Select value={gameFilter} onValueChange={setGameFilter}>
                  <SelectTrigger className="w-36" data-testid="game-filter">
                    <SelectValue placeholder="All Games" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Games</SelectItem>
                    {allGames.map((game) => (
                      <SelectItem key={game} value={game}>
                        {game}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Platform Filter */}
              <div className="flex items-center gap-2">
                <MonitorSmartphone className="w-4 h-4 text-slate-500" />
                <Select value={platformFilter} onValueChange={setPlatformFilter}>
                  <SelectTrigger className="w-36" data-testid="platform-filter">
                    <SelectValue placeholder="All Platforms" />
                  </SelectTrigger>
                  <SelectContent>
                    {PLATFORM_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Time Period Filter */}
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-slate-500" />
                <Select value={timeFilter} onValueChange={setTimeFilter}>
                  <SelectTrigger className="w-44" data-testid="time-filter">
                    <SelectValue placeholder="All Times" />
                  </SelectTrigger>
                  <SelectContent>
                    {TIME_PERIOD_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Day of Week Filter */}
              <div className="flex items-center gap-2">
                <Select value={dayFilter} onValueChange={setDayFilter}>
                  <SelectTrigger className="w-36" data-testid="day-filter">
                    <SelectValue placeholder="All Days" />
                  </SelectTrigger>
                  <SelectContent>
                    {DAY_OF_WEEK_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Bulk Booking Filter */}
              <div className="flex items-center gap-2">
                <Select value={bulkFilter} onValueChange={setBulkFilter}>
                  <SelectTrigger className="w-48" data-testid="bulk-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BULK_FILTER_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="p-6 bg-gradient-to-br from-green-50 to-white border-green-200">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-slate-600 font-medium mb-1">Total Revenue</p>
                  <p className="font-heading font-bold text-3xl text-slate-900">
                    ₹{dailyReport?.total_revenue?.toLocaleString('en-IN') || 0}
                  </p>
                </div>
                <div className="bg-green-100 text-green-700 p-3 rounded-lg">
                  <DollarSign className="w-6 h-6" strokeWidth={2} />
                </div>
              </div>
            </Card>

            <Card className="p-6 bg-gradient-to-br from-blue-50 to-white border-blue-200">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-slate-600 font-medium mb-1">Total Bookings</p>
                  <p className="font-heading font-bold text-3xl text-slate-900">
                    {dailyReport?.total_bookings || 0}
                  </p>
                </div>
                <div className="bg-blue-100 text-blue-700 p-3 rounded-lg">
                  <CalendarCheck className="w-6 h-6" strokeWidth={2} />
                </div>
              </div>
            </Card>

            <Card className="p-6 bg-gradient-to-br from-purple-50 to-white border-purple-200">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-slate-600 font-medium mb-1">Avg. Booking Value</p>
                  <p className="font-heading font-bold text-3xl text-slate-900">
                    ₹
                    {dailyReport?.total_bookings > 0
                      ? Math.round(dailyReport.total_revenue / dailyReport.total_bookings)
                      : 0}
                  </p>
                </div>
                <div className="bg-purple-100 text-purple-700 p-3 rounded-lg">
                  <DollarSign className="w-6 h-6" strokeWidth={2} />
                </div>
              </div>
            </Card>
          </div>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="p-6 border-slate-200">
            <h2 className="font-heading font-bold text-xl text-slate-900 mb-6">Venue-wise Revenue</h2>
            {venueChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={venueChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" stroke="#64748b" />
                  <YAxis stroke="#64748b" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#ffffff',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                    }}
                    formatter={(value, name) => [
                      name === 'revenue' ? `₹${value}` : value,
                      name === 'revenue' ? 'Revenue' : 'Bookings',
                    ]}
                  />
                  <Legend />
                  <Bar dataKey="revenue" fill="#16a34a" name="Revenue (₹)">
                    <LabelList dataKey="revenue" position="top" formatter={formatCompactINR}
                               style={{ fontSize: 11, fontWeight: 600, fill: '#166534' }} />
                  </Bar>
                  <Bar dataKey="bookings" fill="#86efac" name="Bookings" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-64 flex items-center justify-center text-slate-500">
                No venue data available
              </div>
            )}
          </Card>

          <Card className="p-6 border-slate-200">
            <h2 className="font-heading font-bold text-xl text-slate-900 mb-6">Game-wise Revenue</h2>
            {gameChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={gameChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" stroke="#64748b" />
                  <YAxis stroke="#64748b" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#ffffff',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                    }}
                    formatter={(value, name) => [
                      name === 'revenue' ? `₹${value}` : value,
                      name === 'revenue' ? 'Revenue' : 'Bookings',
                    ]}
                  />
                  <Legend />
                  <Bar dataKey="revenue" fill="#15803d" name="Revenue (₹)">
                    <LabelList dataKey="revenue" position="top" formatter={formatCompactINR}
                               style={{ fontSize: 11, fontWeight: 600, fill: '#14532d' }} />
                  </Bar>
                  <Bar dataKey="bookings" fill="#4ade80" name="Bookings" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-64 flex items-center justify-center text-slate-500">
                No game data available
              </div>
            )}
          </Card>
        </div>

        {/* Platform-wise Revenue Report */}
        <Card className="p-6 border-slate-200">
          <h2 className="font-heading font-bold text-xl text-slate-900 mb-6">Platform-wise Revenue</h2>
          {platformChartData.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={platformChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" stroke="#64748b" />
                  <YAxis stroke="#64748b" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#ffffff',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                    }}
                    formatter={(value, name) => [
                      name === 'revenue' ? `₹${value.toLocaleString('en-IN')}` : value,
                      name === 'revenue' ? 'Revenue' : 'Bookings',
                    ]}
                  />
                  <Legend />
                  <Bar dataKey="revenue" fill="#0ea5e9" name="Revenue (₹)">
                    <LabelList dataKey="revenue" position="top" formatter={formatCompactINR}
                               style={{ fontSize: 11, fontWeight: 600, fill: '#0369a1' }} />
                  </Bar>
                  <Bar dataKey="bookings" fill="#7dd3fc" name="Bookings" />
                </BarChart>
              </ResponsiveContainer>
              <div className="flex flex-col justify-center space-y-2">
                <h3 className="font-semibold text-slate-700 mb-2">Platform Breakdown</h3>
                {platformChartData.map((item, index) => (
                  <div key={item.name} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-4 h-4 rounded"
                        style={{ backgroundColor: ['#0ea5e9', '#06b6d4', '#14b8a6', '#22c55e', '#84cc16', '#eab308'][index % 6] }}
                      />
                      <span className="font-medium text-slate-900">{item.name}</span>
                    </div>
                    <div className="text-right">
                      <span className="font-semibold text-slate-900 block">₹{item.revenue.toLocaleString('en-IN')}</span>
                      <span className="text-xs text-slate-500">{item.bookings} bookings</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-slate-500">
              No platform data available
            </div>
          )}
        </Card>

        {paymentBreakdownData.length > 0 && (
          <Card className="p-6 border-slate-200" data-testid="reports-payment-breakdown">
            <h2 className="font-heading font-bold text-xl text-slate-900 mb-6">Payment Mode Breakdown</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={paymentBreakdownData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value }) => `${name}: ${formatCompactINR(value)}`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {paymentBreakdownData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCompactINR(value)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex flex-col justify-center space-y-3">
                {paymentBreakdownData.map((item, index) => (
                  <div key={item.name} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-4 h-4 rounded" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                      <span className="font-medium text-slate-900">{item.name}</span>
                    </div>
                    <span className="font-semibold text-slate-900">₹{item.value.toLocaleString('en-IN')}</span>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
